import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateSale, UpdateSaleParams, getPaymentFormsForSale } from "@/Api/sales";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { getSelectBankAccounts } from "@/Api/bankAccounts";
import { getCircuitRoutesData } from "@/Api/circuit_routes";
import { TableDataProps } from "@/pages/SalesList";
import { toast } from "sonner";
import { SALE_STATUS } from "@/constants/saleStatus";
import { SALE_TYPE_ID, PAYMENT_FORMS, PIX_STATUS, SALE_STEPS, PAYMENT_STATUS } from "@/utils/constants";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { exceedsMoney } from "@/utils/money";
import { PlusCircle, Trash2, AlertCircle, Loader2 } from "lucide-react";
import { apiAuth } from "@/Api";
import { getBleedBalance } from "@/Api/bleeds";
import { AddressSearch } from "@/components/AddressSearch";
import { GoogleMap, loadGoogleMapsScript } from "@/components/GoogleMap";

interface EditSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  sale: TableDataProps | null;
  onSaleUpdated: () => void;
}

export const EditSaleModal = ({
  isOpen,
  onClose,
  sale,
  onSaleUpdated,
}: EditSaleModalProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UpdateSaleParams>({});
  const [saleTypes, setSaleTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [bankAccounts, setBankAccounts] = useState<
    Array<{ id: number; name: string; uuid: string; surname: string }>
  >([]);
  const [paymentForms, setPaymentForms] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [routes, setRoutes] = useState<Array<{ id: number; title: string }>>(
    []
  );
  const [selectedRoute, setSelectedRoute] = useState<string>("");
  const [newPaymentForm, setNewPaymentForm] = useState<number | null>(null);
  const [newPaymentValue, setNewPaymentValue] = useState<string>("");
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>("");
  const [totalSaleAmount, setTotalSaleAmount] = useState<number>(0);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [clientBalance, setClientBalance] = useState<number>(0);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  // Sangria: unidade do vendedor da venda usa sangria? e saldo de sangria do vendedor
  const [sellerUsesBleed, setSellerUsesBleed] = useState<boolean>(false);
  const [bleedBalance, setBleedBalance] = useState<number>(0);
  const [isLoadingBleed, setIsLoadingBleed] = useState<boolean>(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState<boolean>(false);
  const [clientHasPurchaseVoucher, setClientHasPurchaseVoucher] =
    useState<boolean>(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState<boolean>(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showAddressSearch, setShowAddressSearch] = useState<boolean>(false);
  const [trackingCode, setTrackingCode] = useState<string>("");

  useEffect(() => {
    if (sale) {
      const firstAddress = sale.client?.addresses?.[0];
      const buildAddressName = (addr: any) => {
        if (!addr) return "";
        const parts: string[] = [];
        if (addr.street) parts.push(addr.street);
        if (addr.number) parts.push(addr.number);
        if (addr.neighborhood) parts.push(addr.neighborhood);
        if (addr.city && addr.state) parts.push(`${addr.city}/${addr.state}`);
        else if (addr.city) parts.push(addr.city);
        return parts.join(", ");
      };

      setFormData({
        sale_type_id: sale.sale_type_id,
        bank_account_id: (sale as any).bank_account_id,
        circuit_route_id: (sale as any).circuit_route_id,
        sale_status_id: sale.sale_status_id,
        address: firstAddress
          ? {
              addressName: buildAddressName(firstAddress),
              street: firstAddress.street || "",
              number: firstAddress.number || "",
              complement: firstAddress.complement || "",
              neighborhood: firstAddress.neighborhood || "",
              city: firstAddress.city || "",
              state: firstAddress.state || "",
              zip_code: firstAddress.zip_code || "",
              latitude: firstAddress.latitude ? Number(firstAddress.latitude) : null,
              longitude: firstAddress.longitude ? Number(firstAddress.longitude) : null,
            }
          : undefined,
        payment_forms: sale.payment_forms?.map((pf) => ({
          payment_form_id: pf.payment_form_id,
          value: parseFloat(pf.value),
          installments: (pf as any).number_installments || null,
        })),
      });

      if (firstAddress?.id) {
        setSelectedAddressId(firstAddress.id);
      }
      setShowAddressSearch(false);
      setTrackingCode((sale as any).tracking_code || "");

      const total = parseFloat(sale.total);
      setTotalSaleAmount(total);

      if ((sale as any).circuit_route_id) {
        setSelectedRoute((sale as any).circuit_route_id.toString());
      }
    }
  }, [sale]);

  // Load sale types and bank accounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const today = Mask.getLocalDate();
        const [
          saleTypesResponse,
          bankAccountsResponse,
          paymentFormsResponse,
          routesResponse,
        ] = await Promise.all([
          getSelectSaleTypes(),
          getSelectBankAccounts(),
          getPaymentFormsForSale(),
          getCircuitRoutesData({
            start_date: today,
            end_date: today,
          }),
        ]);

        if (saleTypesResponse.ok && saleTypesResponse.data?.response) {
          setSaleTypes(saleTypesResponse.data.response);
        }

        if (bankAccountsResponse.ok && bankAccountsResponse.data?.response) {
          setBankAccounts(bankAccountsResponse.data.response);
        }

        if (paymentFormsResponse.ok && paymentFormsResponse.data?.response) {
          setPaymentForms(paymentFormsResponse.data.response);
        }

        if (routesResponse.ok && routesResponse.data?.response) {
          setRoutes(routesResponse.data.response);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Erro ao carregar dados");
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setIsGoogleMapsLoaded(true);
      } catch (error) {
        console.error("Error loading Google Maps:", error);
      }
    };

    if (isOpen) {
      loadMaps();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!sale?.client) {
        setClientHasPurchaseVoucher(false);
        return;
      }

      try {
        const clientSalePurchaseVoucher =
          sale?.client?.has_purchase_voucher || false;
        if (clientSalePurchaseVoucher) {
          setClientHasPurchaseVoucher(clientSalePurchaseVoucher);
        } else {
          setClientHasPurchaseVoucher(false);
        }
      } catch (error) {
        console.error("Error fetching client data:", error);
        setClientHasPurchaseVoucher(false);
      }
    };

    if (isOpen && sale) {
      fetchClientData();
    }
  }, [isOpen, sale]);

  const fetchClientBalance = useCallback(
    async (balanceOriginTypeId?: number) => {
      if (!sale?.client_id) return;

      setIsLoadingBalance(true);
      try {
        const url = `/clients/${
          (sale.client as any)?.uuid || sale.client_id
        }/balance`;
        const params = balanceOriginTypeId
          ? { balance_origin_type_id: balanceOriginTypeId }
          : undefined;

        const { data } = await apiAuth.get(url, { params });

        if (data?.response !== undefined) {
          const balance = Number(data.response);
          setClientBalance(balance);
        } else {
          setClientBalance(0);
        }
      } catch (error) {
        console.error("Error fetching client balance:", error);
        setClientBalance(0);
      } finally {
        setIsLoadingBalance(false);
      }
    },
    [sale?.client_id, sale?.client]
  );

  // Calcular valor restante
  useEffect(() => {
    if (formData.payment_forms && totalSaleAmount > 0) {
      const totalPaid = formData.payment_forms.reduce(
        (sum, form) => sum + form.value,
        0
      );
      setRemainingAmount(Number((totalSaleAmount - totalPaid).toFixed(2)));
    }
  }, [formData.payment_forms, totalSaleAmount]);

  useEffect(() => {
    const balanceBasedForms =
      formData.payment_forms?.filter(
        (form) => form.payment_form_id === PAYMENT_FORMS.REINVESTMENT
      ) || [];

    const usedBalanceAmount = balanceBasedForms.reduce(
      (sum, form) => sum + form.value,
      0
    );

    const calculatedAvailableBalance = clientBalance - usedBalanceAmount;
    setAvailableBalance(calculatedAvailableBalance);
  }, [clientBalance, formData.payment_forms]);

  // Buscar saldo do cliente quando necessário
  useEffect(() => {
    if (sale?.client_id) {
      // Verifica se tem REINVESTMENT selecionado
      const hasReinvestment =
        newPaymentForm === PAYMENT_FORMS.REINVESTMENT ||
        formData.payment_forms?.some(
          (form) => form.payment_form_id === PAYMENT_FORMS.REINVESTMENT
        );

      if (hasReinvestment) {
        fetchClientBalance(undefined);
      }
    }
  }, [
    sale?.client_id,
    newPaymentForm,
    formData.payment_forms,
    fetchClientBalance,
  ]);

  // Fetch bank accounts when deposit is selected
  useEffect(() => {
    if (newPaymentForm === PAYMENT_FORMS.DEPOSIT) {
      if (bankAccounts.length === 0) {
        // Bank accounts already loaded in main useEffect
      }
    } else {
      setSelectedBankAccount("");
    }
  }, [newPaymentForm, bankAccounts.length]);

  const handleAddPaymentForm = () => {
    if (!newPaymentForm || !newPaymentValue) return;

    // Verificar se precisa de conta bancária para depósito
    if (newPaymentForm === PAYMENT_FORMS.DEPOSIT && !selectedBankAccount) {
      toast.error("Selecione uma conta bancária para depósito");
      return;
    }

    let value: number;
    try {
      value = parseFloat(Mask.transformCurrencyInFloat(newPaymentValue));
      if (isNaN(value) || value <= 0) {
        toast.error("Valor inválido");
        return;
      }
    } catch (error) {
      toast.error("Valor inválido");
      return;
    }

    if (exceedsMoney(value, remainingAmount)) {
      toast.error(
        `O valor não pode exceder o valor restante da venda (${Mask.formatCurrency(
          remainingAmount
        )})`
      );
      return;
    }

    // Valor igual ao restante (na precisão de centavos): usa o restante exato
    if (!exceedsMoney(remainingAmount, value) && !exceedsMoney(value, remainingAmount)) {
      value = remainingAmount;
    }

    if (
      newPaymentForm === PAYMENT_FORMS.REINVESTMENT &&
      exceedsMoney(value, availableBalance)
    ) {
      toast.error(
        `Saldo de reinvestimento insuficiente. Disponível: ${Mask.formatCurrency(
          availableBalance
        )}`
      );
      return;
    }

    if (
      newPaymentForm === PAYMENT_FORMS.MONEY_BLEED &&
      exceedsMoney(value, availableBleedBalance)
    ) {
      toast.error(
        `Saldo de sangria insuficiente. Disponível: ${Mask.formatCurrency(
          Math.max(0, availableBleedBalance)
        )}`
      );
      return;
    }

    const paymentFormItem: any = {
      payment_form_id: newPaymentForm,
      value,
      installments: null,
    };

    // Adicionar ID da conta bancária se for depósito
    if (newPaymentForm === PAYMENT_FORMS.DEPOSIT) {
      paymentFormItem.bank_account_id = selectedBankAccount;
    }

    const updatedForms = [...(formData.payment_forms || []), paymentFormItem];
    setFormData((prev) => ({ ...prev, payment_forms: updatedForms }));

    setNewPaymentForm(null);
    setNewPaymentValue("");
    setSelectedBankAccount("");
  };

  const handleRemovePaymentForm = (index: number) => {
    if (!formData.payment_forms) return;
    const updatedForms = formData.payment_forms.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, payment_forms: updatedForms }));
  };

  const handleEditPaymentFormValue = (index: number, newValue: string) => {
    if (!formData.payment_forms) return;

    let value: number;
    try {
      value = parseFloat(Mask.transformCurrencyInFloat(newValue));
      if (isNaN(value) || value <= 0) return;
    } catch (error) {
      return;
    }

    const updatedForms = [...formData.payment_forms];
    updatedForms[index].value = value;
    setFormData((prev) => ({ ...prev, payment_forms: updatedForms }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sale) return;

    setLoading(true);
    try {
      // Se o pagamento está completo, envia apenas sale_type_id (e address se for delivery/sedex)
      const updateData: UpdateSaleParams = isPaymentComplete
        ? {
            sale_type_id: formData.sale_type_id,
            ...(formData.address && { address: formData.address }),
            ...(formData.sale_type_id === SALE_TYPE_ID.SEDEX && { tracking_code: trackingCode }),
          }
        : {
            ...formData,
            sale_status_id: SALE_STATUS.EDITED,
            ...(formData.sale_type_id === SALE_TYPE_ID.SEDEX && { tracking_code: trackingCode }),
          };

      // DEBUG: Log para verificar dados enviados
      console.log('[DEBUG EditSaleModal] Dados sendo enviados:', {
        isPaymentComplete,
        sale_type_id: updateData.sale_type_id,
        circuit_route_id: updateData.circuit_route_id,
        hasAddress: !!updateData.address,
        address: updateData.address,
        selectedRoute: selectedRoute,
        hasPaymentForms: !!updateData.payment_forms,
      });

      const { ok } = await updateSale(sale.uuid, updateData);

      if (ok) {
        toast.success("Venda editada com sucesso!");
        onSaleUpdated();
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!sale) return null;

  // Verificar se é tipo especial (endosso ou sedex)
  const isSpecialSaleType =
    formData.sale_type_id === SALE_TYPE_ID.ENDOSSO ||
    formData.sale_type_id === SALE_TYPE_ID.SEDEX;

  const availablePaymentForms = (
    isSpecialSaleType
      ? paymentForms.filter(
          (form) =>
            form.id === PAYMENT_FORMS.PIX ||
            form.id === PAYMENT_FORMS.PIX_BTG ||
            form.id === PAYMENT_FORMS.REINVESTMENT ||
            form.id === PAYMENT_FORMS.VALE ||
            form.id === PAYMENT_FORMS.PIX_MAQUINETA ||
            form.id === PAYMENT_FORMS.DEPOSIT ||
            form.id === PAYMENT_FORMS.PIX_CNPJ ||
            form.id === PAYMENT_FORMS.MONEY ||
            form.id === PAYMENT_FORMS.MONEY_BLEED ||
            form.id === PAYMENT_FORMS.ADVANCE_PAYMENT
        )
      : paymentForms
  ).filter((form) => {
    // Sangria: unidade com sangria vê só "Dinheiro - Sangria"; as demais só "Dinheiro"
    if (form.id === PAYMENT_FORMS.MONEY) {
      return !sellerUsesBleed;
    }
    if (form.id === PAYMENT_FORMS.MONEY_BLEED) {
      return sellerUsesBleed;
    }
    if (form.id === PAYMENT_FORMS.VALE) {
      return clientHasPurchaseVoucher === true;
    }
    if (form.id === PAYMENT_FORMS.ADVANCE_PAYMENT) {
      const hasAdvancePayment = formData.payment_forms?.some(
        (f) => f.payment_form_id === PAYMENT_FORMS.ADVANCE_PAYMENT
      );
      if (hasAdvancePayment) {
        return false;
      }
    }
    return true;
  });

  // Sangria: busca saldo/unidade do vendedor da venda ao abrir
  useEffect(() => {
    if (!isOpen || !sale?.seller_id) return;
    let cancelled = false;
    setIsLoadingBleed(true);
    getBleedBalance(sale.seller_id)
      .then(({ data, ok }) => {
        if (cancelled) return;
        setSellerUsesBleed(ok ? !!data?.response?.has_bleed : false);
        setBleedBalance(ok ? Number(data?.response?.balance || 0) : 0);
      })
      .finally(() => !cancelled && setIsLoadingBleed(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, sale?.seller_id]);

  // Na edição o back estorna a sangria das formas antigas antes de debitar as novas,
  // então o valor que esta venda já usou volta a ficar disponível.
  const originalBleedAmount = (sale?.payment_forms || [])
    .filter((pf) => pf.payment_form_id === PAYMENT_FORMS.MONEY_BLEED)
    .reduce((sum, pf) => sum + parseFloat(String(pf.value)), 0);
  const usedBleedAmount = (formData.payment_forms || [])
    .filter((pf) => pf.payment_form_id === PAYMENT_FORMS.MONEY_BLEED)
    .reduce((sum, pf) => sum + Number(pf.value), 0);
  const availableBleedBalance =
    Math.round((bleedBalance + originalBleedAmount - usedBleedAmount) * 100) / 100;

  // Verificar se uma forma de pagamento PIX está paga
  const isPixPaymentPaid = (paymentFormId: number, value: number): boolean => {
    const isPixPayment =
      paymentFormId === PAYMENT_FORMS.PIX ||
      paymentFormId === PAYMENT_FORMS.PIX_BTG ||
      paymentFormId === PAYMENT_FORMS.PIX_MAQUINETA ||
      paymentFormId === PAYMENT_FORMS.PIX_CNPJ;

    if (!isPixPayment || !sale?.transactions) return false;

    // Procura transação PIX com mesmo valor que está paga
    const paidTransaction = sale.transactions.find(
      (t) =>
        t.pix_copy_and_paste &&
        t.status === PIX_STATUS.PAID &&
        Math.abs(parseFloat(t.amount) - value) < 0.01
    );

    return !!paidTransaction;
  };

  // Conta quantas transações PIX foram pagas
  const paidPixCount = sale?.transactions?.filter(
    (t) => t.pix_copy_and_paste && t.status === PIX_STATUS.PAID
  ).length || 0;

  // Verifica se o step de pagamento foi aprovado
  const isPaymentStepApproved = sale?.process_step_sales?.some(
    (step) => step.sale_step_id === SALE_STEPS.WAITING_PAYMENT && step.process_status_id === PAYMENT_STATUS.COMPLETED
  );

  // Verifica se o pagamento está completo (não pode editar formas de pagamento)
  // Só bloqueia se o step de pagamento foi aprovado E o valor está zerado
  const isPaymentComplete = isPaymentStepApproved && remainingAmount === 0 && totalSaleAmount > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Venda #{sale.id}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sale_type_id">Tipo de Venda</Label>
              <Select
                value={
                  formData.sale_type_id && !isNaN(formData.sale_type_id)
                    ? formData.sale_type_id.toString()
                    : ""
                }
                onValueChange={(value) => {
                  const numValue = parseInt(value);
                  if (!isNaN(numValue)) {
                    setFormData((prev) => {
                      const newData = {
                        ...prev,
                        sale_type_id: numValue,
                      };
                      if (numValue === SALE_TYPE_ID.DELIVERY && !prev.address) {
                        newData.address = {
                          addressName: "",
                          street: "",
                          number: "",
                          complement: "",
                          neighborhood: "",
                          city: "",
                          state: "",
                          zip_code: "",
                          latitude: null,
                          longitude: null,
                        };
                      }
                      return newData;
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {saleTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_account_id">Conta Bancária</Label>
              <Select
                value={
                  formData.bank_account_id && !isNaN(formData.bank_account_id)
                    ? formData.bank_account_id.toString()
                    : "0"
                }
                onValueChange={(value) => {
                  const numValue = value === "0" ? null : parseInt(value);
                  setFormData((prev) => ({
                    ...prev,
                    bank_account_id: numValue,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Nenhuma</SelectItem>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id.toString()}>
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.sale_type_id === SALE_TYPE_ID.DELIVERY && (
              <div className="space-y-2">
                <Label htmlFor="circuit_route_id">Rota</Label>
                <Select
                  value={selectedRoute}
                  onValueChange={(value) => {
                    setSelectedRoute(value);
                    setFormData((prev) => ({
                      ...prev,
                      circuit_route_id: parseInt(value),
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a rota" />
                  </SelectTrigger>
                  <SelectContent>
                    {routes.map((route) => (
                      <SelectItem key={route.id} value={route.id.toString()}>
                        {route.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {(formData.sale_type_id === SALE_TYPE_ID.DELIVERY || formData.sale_type_id === SALE_TYPE_ID.SEDEX) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">
                    {formData.sale_type_id === SALE_TYPE_ID.SEDEX ? "Endereço de Envio (Sedex)" : "Endereço de Entrega"}
                  </h3>

                  {/* Campo de código de rastreio para SEDEX */}
                  {formData.sale_type_id === SALE_TYPE_ID.SEDEX && (
                    <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg space-y-2">
                      <Label htmlFor="tracking-code-edit" className="font-medium text-orange-700 dark:text-orange-300 flex items-center gap-2">
                        <span>📦</span>
                        Código de Rastreio (Sedex)
                      </Label>
                      <Input
                        id="tracking-code-edit"
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                        placeholder="Ex: BR123456789BR"
                        className="uppercase"
                        maxLength={50}
                      />
                      <p className="text-xs text-orange-600 dark:text-orange-400">
                        Informe o código de rastreio dos Correios para acompanhamento da entrega
                      </p>
                    </div>
                  )}

                  {sale?.client?.addresses && sale.client.addresses.length > 0 && (
                    <div className="space-y-2">
                      <Label>Endereços do Cliente</Label>
                      <Select
                        value={selectedAddressId?.toString() || ""}
                        onValueChange={(value) => {
                          if (value === "search") {
                            setShowAddressSearch(true);
                            setSelectedAddressId(null);
                            return;
                          }

                          setShowAddressSearch(false);
                          const addressId = Number(value);
                          setSelectedAddressId(addressId);

                          const selectedAddr = sale?.client?.addresses?.find(
                            (addr: any) => addr.id === addressId
                          );

                          if (selectedAddr) {
                            const buildAddressName = (addr: any) => {
                              if (!addr) return "";
                              const parts: string[] = [];
                              if (addr.street) parts.push(addr.street);
                              if (addr.number) parts.push(addr.number);
                              if (addr.neighborhood) parts.push(addr.neighborhood);
                              if (addr.city && addr.state) parts.push(`${addr.city}/${addr.state}`);
                              else if (addr.city) parts.push(addr.city);
                              return parts.join(", ");
                            };

                            setFormData((prev) => ({
                              ...prev,
                              address: {
                                addressName: buildAddressName(selectedAddr),
                                street: selectedAddr.street || "",
                                number: selectedAddr.number || "",
                                complement: selectedAddr.complement || "",
                                neighborhood: selectedAddr.neighborhood || "",
                                city: selectedAddr.city || "",
                                state: selectedAddr.state || "",
                                zip_code: selectedAddr.zip_code || "",
                                latitude: selectedAddr.latitude ? Number(selectedAddr.latitude) : null,
                                longitude: selectedAddr.longitude ? Number(selectedAddr.longitude) : null,
                              },
                            }));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um endereço" />
                        </SelectTrigger>
                        <SelectContent>
                          {sale.client.addresses.map((addr: any) => {
                            const parts: string[] = [];
                            if (addr.street) parts.push(addr.street);
                            if (addr.number) parts.push(addr.number);
                            if (addr.neighborhood) parts.push(addr.neighborhood);
                            const addressLabel = parts.length > 0
                              ? parts.join(", ")
                              : (addr.latitude ? "Localização salva (ver mapa)" : "Endereço não informado");

                            return (
                              <SelectItem key={addr.id} value={addr.id.toString()}>
                                {addressLabel}
                              </SelectItem>
                            );
                          })}
                          <SelectItem value="search">
                            🔍 Buscar novo endereço...
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {(showAddressSearch || !sale?.client?.addresses?.length) && (
                    <div className="space-y-2">
                      <Label>Buscar Endereço</Label>
                      <AddressSearch
                        onAddressSelect={(selectedAddress) => {
                          setFormData((prev) => ({
                            ...prev,
                            address: {
                              addressName: selectedAddress.addressName,
                              street: selectedAddress.street,
                              number: selectedAddress.number,
                              complement: prev.address?.complement || "",
                              neighborhood: selectedAddress.neighborhood,
                              city: selectedAddress.city,
                              state: selectedAddress.state,
                              zip_code: selectedAddress.zip_code,
                              latitude: parseFloat(selectedAddress.latitude) || null,
                              longitude: parseFloat(selectedAddress.longitude) || null,
                            },
                          }));
                          setSelectedAddressId(null);
                        }}
                        defaultValue=""
                        placeholder="Digite o endereço para buscar..."
                      />
                      {sale?.client?.addresses?.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowAddressSearch(false);
                            if (sale?.client?.addresses?.[0]?.id) {
                              setSelectedAddressId(sale.client.addresses[0].id);
                            }
                          }}
                          className="text-sm text-muted-foreground"
                        >
                          ← Voltar para endereços salvos
                        </Button>
                      )}
                    </div>
                  )}

                  {formData.address?.latitude && formData.address?.longitude && (
                    <div className="space-y-2">
                      {isGoogleMapsLoaded ? (
                        <GoogleMap
                          latitude={Number(formData.address.latitude)}
                          longitude={Number(formData.address.longitude)}
                          address={formData.address.addressName}
                          className="h-48"
                        />
                      ) : (
                        <div className="w-full h-48 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span>Carregando mapa...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="complement">Complemento</Label>
                    <Input
                      id="complement"
                      value={formData.address?.complement || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address!,
                            complement: e.target.value,
                          },
                        }))
                      }
                      placeholder="Apt, Bloco, Casa, etc."
                    />
                  </div>

                  {formData.address?.addressName && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-sm">
                      <p className="font-medium text-green-700 dark:text-green-300">✓ Endereço selecionado:</p>
                      <p className="text-green-600 dark:text-green-400">{formData.address.addressName}</p>
                      {formData.address.latitude && formData.address.longitude && (
                        <p className="text-xs text-green-500 mt-1">
                          Coordenadas: {Number(formData.address.latitude).toFixed(6)}, {Number(formData.address.longitude).toFixed(6)}
                        </p>
                      )}
                    </div>
                  )}

                  {!formData.address?.latitude && !formData.address?.longitude && formData.address?.addressName && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md text-sm">
                      <p className="font-medium text-yellow-700 dark:text-yellow-300">⚠ Atenção:</p>
                      <p className="text-yellow-600 dark:text-yellow-400">
                        Este endereço não possui coordenadas. Selecione outro endereço ou busque um novo.
                      </p>
                    </div>
                  )}
                </div>
              )}

          {formData.sale_type_id && formData.sale_type_id !== SALE_TYPE_ID.DELIVERY && formData.sale_type_id !== SALE_TYPE_ID.SEDEX && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-md">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                💡 Endereço de entrega só pode ser editado para vendas do
                tipo "Entrega" ou "Sedex"
              </p>
            </div>
          )}

          {/* Seção de Formas de Pagamento */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Formas de Pagamento</h3>

            {isPaymentComplete && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <span className="text-amber-700 dark:text-amber-300">
                  Pagamento completo. Somente o tipo de venda pode ser alterado.
                </span>
              </div>
            )}

            {!isPaymentComplete && paidPixCount > 0 && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-green-600" />
                <span className="text-green-700 dark:text-green-300">
                  {paidPixCount} pagamento(s) PIX já confirmado(s) não pode(m) ser editado(s) ou removido(s).
                </span>
              </div>
            )}

            {isSpecialSaleType && (
              <div className="text-amber-600 font-medium mb-4 flex gap-2 items-center">
                <AlertCircle className="h-4 w-4" />
                <span>
                  Para este tipo de venda, apenas o pagamento via PIX, PIX
                  Maquineta, PIX CNPJ, Reinvestimento ou Depósito é permitido.
                </span>
              </div>
            )}

            {/* Adicionar nova forma de pagamento */}
            {!isPaymentComplete && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payment-form">Forma de pagamento</Label>
                  <Select
                    value={newPaymentForm?.toString() || ""}
                    onValueChange={(value) => setNewPaymentForm(Number(value))}
                  >
                    <SelectTrigger id="payment-form">
                      <SelectValue placeholder="Selecione a forma de pagamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePaymentForms.map((form) => (
                        <SelectItem key={form.id} value={form.id.toString()}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-value">Valor</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="payment-value"
                      typeMask="currency"
                      value={newPaymentValue}
                      onChange={(e) => setNewPaymentValue(e.target.value)}
                      placeholder="Valor"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleAddPaymentForm}
                      disabled={
                        !newPaymentForm ||
                        !newPaymentValue ||
                        (newPaymentForm === PAYMENT_FORMS.DEPOSIT &&
                          !selectedBankAccount)
                      }
                    >
                      <PlusCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Conta bancária para depósito */}
            {!isPaymentComplete && newPaymentForm === PAYMENT_FORMS.DEPOSIT && (
              <div className="space-y-2">
                <Label htmlFor="bank-account">
                  Conta bancária para depósito
                </Label>
                <Select
                  value={selectedBankAccount}
                  onValueChange={(value) => setSelectedBankAccount(value)}
                >
                  <SelectTrigger id="bank-account">
                    <SelectValue placeholder="Selecione a conta bancária" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.uuid} value={account.uuid}>
                        {account.name} - {account?.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Informações de reinvestimento */}
            {!isPaymentComplete && newPaymentForm === PAYMENT_FORMS.REINVESTMENT && (
              <div
                className={`text-sm mt-2 ${
                  availableBalance > 0 ? "text-green-600" : "text-amber-600"
                }`}
              >
                {isLoadingBalance
                  ? "Carregando saldo..."
                  : `Saldo disponível para reinvestimento: ${Mask.formatCurrency(
                      availableBalance
                    )} (Saldo total: ${Mask.formatCurrency(clientBalance)})`}
              </div>
            )}

            {/* Saldo de sangria do vendedor */}
            {!isPaymentComplete && newPaymentForm === PAYMENT_FORMS.MONEY_BLEED && (
              <div
                className={`text-sm mt-2 font-medium ${
                  availableBleedBalance > 0 ? "text-green-600" : "text-amber-600"
                }`}
              >
                {isLoadingBleed
                  ? "Carregando saldo de sangria..."
                  : `Saldo disponível sangria: ${Mask.formatCurrency(
                      Math.max(0, availableBleedBalance)
                    )} (Saldo total: ${Mask.formatCurrency(bleedBalance)})`}
              </div>
            )}

            {/* Resumo dos valores */}
            <div className="space-y-2">
              <div className="text-sm font-medium">
                Valor total da venda: {Mask.formatCurrency(totalSaleAmount)}
              </div>
              <div
                className={`text-sm font-medium ${
                  remainingAmount < 0
                    ? "text-red-500"
                    : remainingAmount > 0
                    ? "text-amber-600"
                    : "text-green-600"
                }`}
              >
                {remainingAmount === 0
                  ? "✓ Pagamento completo"
                  : remainingAmount > 0
                  ? `Falta pagar: ${Mask.formatCurrency(remainingAmount)}`
                  : `Valor excede o total em: ${Mask.formatCurrency(
                      Math.abs(remainingAmount)
                    )}`}
              </div>
            </div>

            {/* Lista de formas de pagamento */}
            {formData.payment_forms && formData.payment_forms.length > 0 && (
              <div className="space-y-2">
                <Label>Formas de pagamento selecionadas</Label>
                <div className="border rounded-md divide-y">
                  {formData.payment_forms.map((form, index) => {
                    const paymentFormName =
                      paymentForms.find((pf) => pf.id === form.payment_form_id)
                        ?.name || "Forma desconhecida";

                    let bankAccountInfo = "";
                    if (
                      form.payment_form_id === PAYMENT_FORMS.DEPOSIT &&
                      (form as any).bank_account_id
                    ) {
                      const account = bankAccounts.find(
                        (acc) => acc.uuid === (form as any).bank_account_id
                      );
                      if (account) {
                        bankAccountInfo = ` (${account.name})`;
                      }
                    }

                    const isPaid = isPixPaymentPaid(form.payment_form_id, form.value);
                    const isLocked = isPaid || isPaymentComplete;

                    return (
                      <div
                        key={index}
                        className={cn(
                          "p-3 flex justify-between items-center",
                          isPaid && "bg-green-50 dark:bg-green-900/20"
                        )}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <span className="font-medium min-w-[120px]">
                            {paymentFormName}
                            {bankAccountInfo}
                            {isPaid && (
                              <span className="ml-2 text-xs text-green-600 font-normal">
                                (Pago)
                              </span>
                            )}
                          </span>
                          <Input
                            type="text"
                            typeMask="currency"
                            value={Mask.formatCurrency(form.value)}
                            onChange={(e) =>
                              handleEditPaymentFormValue(index, e.target.value)
                            }
                            className="w-32"
                            disabled={isLocked}
                          />
                        </div>
                        {!isPaymentComplete && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemovePaymentForm(index)}
                            disabled={isPaid}
                            title={isPaid ? "Não é possível remover pagamentos já confirmados" : "Remover"}
                          >
                            <Trash2 className={cn("h-4 w-4", isPaid ? "text-gray-300" : "text-red-500")} />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className={cn(colors.bg_primary)}
            >
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
